import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../../store/store";
import {
  fetchLastRepairSeqAsync,
  createRepairAsync,
  fetchRepairsAsync,
  clearMessages,
} from "../../../../../store/Slice/repairSlice";
import { toast } from "react-toastify";
import { MdDeleteOutline } from "react-icons/md";
import { motion } from "motion/react";

const months = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

type SingleFormData = {
  warrantyType: "CUW" | "COW" | "";
  requestType: "repair" | "replacement" | "";
  instrumentName: string;
  quantity: number;
  companyName: string;
  reseller: string;
  serverId: string;
  receivedBy: string;
  handoverPerson: string;
  departmentPerson: string;
  issueDescription: string;
  receivingDate: string;
  expectedDeliveryDate: string;
  remark: string;
  orderNumber?: string;
  seq?: number;
  createdBy?: { username: string; employeeId: string };
};

const emptyForm = (): SingleFormData => ({
  warrantyType: "",
  requestType: "",
  instrumentName: "",
  quantity: 1,
  companyName: "",
  reseller: "",
  serverId: "",
  receivedBy: "",
  handoverPerson: "",
  departmentPerson: "",
  issueDescription: "",
  receivingDate: new Date().toISOString().slice(0, 10),
  expectedDeliveryDate: "",
  remark: "",
});

type Props = { onCancel: () => void };

const RepairReplacementForm: React.FC<Props> = ({ onCancel }) => {
  const dispatch = useDispatch();
  const { lastSeq, lastOrder, error, message } = useSelector(
    (state: RootState) => state.repairs
  );

  const user = useSelector((state: RootState) => state.auth?.user) ||
    JSON.parse(localStorage.getItem("user") || "null");

  const [forms, setForms] = useState<SingleFormData[]>([emptyForm()]);
  const [submitting, setSubmitting] = useState(false);
  const [touchedForms, setTouchedForms] = useState<Set<number>>(new Set());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [baseSeq, setBaseSeq] = useState<{
    repair: number;
    replacement: number;
  }>({
    repair: 0,
    replacement: 0,
  });


  useEffect(() => {
    dispatch(clearMessages() as any);
  }, [dispatch]);

  useEffect(() => {
    if (message) {
      toast.success(message);
      dispatch(clearMessages() as any);
    }
    if (error) {
      toast.error(error);
      dispatch(clearMessages() as any);
    }
  }, [message, error, dispatch]);

  useEffect(() => {
    const now = new Date();
    const month = now.getMonth();

    // month change → reset
    if (month !== currentMonth) {
      setBaseSeq({ repair: 0, replacement: 0 });
      setCurrentMonth(month);
    }

    // 🔥 requestType-wise seq fetch
    Promise.all([
      dispatch(fetchLastRepairSeqAsync({ requestType: "repair" }) as any),
      dispatch(fetchLastRepairSeqAsync({ requestType: "replacement" }) as any),
    ]).then(([repairRes, replacementRes]: any) => {
      setBaseSeq({
        repair: repairRes?.payload?.lastSeq || 0,
        replacement: replacementRes?.payload?.lastSeq || 0,
      });
    });
  }, [dispatch, currentMonth]);


  const computePreview = (form: SingleFormData, index: number) => {
    if (!form.warrantyType || !form.requestType) return "";

    const type = form.requestType; // repair | replacement
    const seq = baseSeq[type] + index + 1;

    const now = new Date();
    const mon = months[now.getMonth()];
    const yr = now.getFullYear() % 100;
    const suffix = type === "replacement" ? "R" : "";

    return `${form.warrantyType}${suffix}/QESPL/${mon}/${yr}/${String(seq).padStart(2, "0")}`;
  };

  const handleAddProduct = () => {
    setForms([...forms, emptyForm()]);
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 100);
  };

  const handleRemoveProduct = (index: number) => {
    if (forms.length === 1) {
      toast.info("At least one product form is required");
      return;
    }
    const updated = forms.filter((_, i) => i !== index);
    setForms(updated);
    const newTouched = new Set<number>();
    touchedForms.forEach((i) => {
      if (i < index) newTouched.add(i);
      else if (i > index) newTouched.add(i - 1);
    });
    setTouchedForms(newTouched);
    toast.success("Product form removed");
  };

  const handleChange = (index: number, field: keyof SingleFormData, value: any) => {
    const updated = [...forms];
    updated[index][field] = value;
    setForms(updated);
    setTouchedForms((prev) => new Set(prev).add(index));
  };

  const validateForm = (form: SingleFormData, index: number): string | null => {
    if (!form.warrantyType) return `Please select warranty type for product #${index + 1}`;
    if (!form.requestType) return `Please select request type for product #${index + 1}`;
    if (!form.instrumentName.trim()) return `Instrument Name is required for product #${index + 1}`;
    if (!form.companyName.trim()) return `Company name is required for product #${index + 1}`;
    if (!form.receivingDate) return `Receiving date is required for product #${index + 1}`;
    if (!form.expectedDeliveryDate) return `Expected delivery date is required for product #${index + 1}`;
    const receivingDate = new Date(form.receivingDate);
    const expectedDate = new Date(form.expectedDeliveryDate);
    if (expectedDate < receivingDate)
      return `Expected delivery date cannot be before receiving date for product #${index + 1}`;
    return null;
  };

  const handleSubmitAll = async (e: React.FormEvent) => {
    e.preventDefault();

    for (let i = 0; i < forms.length; i++) {
      const validationError = validateForm(forms[i], i);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const type = forms[i].requestType!;
      forms[i].seq = baseSeq[type] + i + 1;
      forms[i].orderNumber = computePreview(forms[i], i);
      forms[i].createdBy = {
        username: user?.username || "System",
        employeeId: user?.employeeId || "N/A",
      };
    }

    try {
      setSubmitting(true);
      const payload = forms.length === 1 ? forms[0] : forms;
      await dispatch(createRepairAsync(payload) as any).unwrap();
      dispatch(fetchRepairsAsync() as any);
      toast.success(`Successfully submitted ${forms.length} product(s)!`);
      onCancel();
    } catch (err: any) {
      toast.error(err?.message || "Failed to create repair(s)");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-h-[90vh] overflow-y-auto p-4">
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1, transition: { duration: 0.3 } }}
        className="bg-white dark:bg-zinc-800 w-full max-w-5xl mx-auto rounded-2xl p-6 shadow-xl border border-gray-300 dark:border-zinc-700 overflow-y-auto max-h-[85vh]"
      >
        <div className="flex justify-between items-center w-full">
          <h1 className="text-black text-center w-full dark:text-white font-bold lg:text-lg xl:text-lg text-sm mb-5 uppercase">
            Repair / Replacement Request
          </h1>
          <button
            className="text-gray-700 dark:text-white text-4xl px-3 py-2 rounded-lg cursor-pointer"
            onClick={onCancel}
          >
            &times;
          </button>
        </div>

        <form className="flex flex-col gap-4" onSubmit={handleSubmitAll}>
          {forms.map((form, index) => (
            <section key={index} className="flex flex-col gap-2 lg:text-lg xl:text-lg text-sm">
              <div className="flex justify-between items-center">
                <h1 className="text-start font-bold text-xl text-blue-500 mb-4 uppercase">
                  Product #{String(index + 1).padStart(2, "0")}
                </h1>
                {forms.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveProduct(index)}
                    className="text-red-600 hover:text-red-700 text-xl hover:bg-red-200 p-2 duration-300 cursor-pointer rounded-lg mb-4"
                  >
                    <MdDeleteOutline />
                  </button>
                )}
              </div>

              <div className="border-2 rounded-lg p-5 shadow-lg">
                {/* Warranty Type */}
                <div className="relative z-0 w-full mb-5 group">
                  <select
                    name="warrantyType"
                    id={`warranty_type_${index}`}
                    value={form.warrantyType}
                    onChange={(e) => handleChange(index, "warrantyType", e.target.value)}
                    className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                    required
                  >
                    <option value="">-- Select Warranty Type --</option>
                    <option value="CUW">Under Warranty (CUW)</option>
                    <option value="COW">Out of Warranty (COW)</option>
                  </select>
                  <label
                    htmlFor={`warranty_type_${index}`}
                    className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                  >
                    Warranty Type <span className="text-red-500 m-2">*</span>
                  </label>
                </div>

                {/* Request Type */}
                <div className="relative z-0 w-full mb-5 group">
                  <select
                    name="requestType"
                    id={`request_type_${index}`}
                    value={form.requestType}
                    onChange={(e) => handleChange(index, "requestType", e.target.value)}
                    className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                    required
                  >
                    <option value="">-- Select Request Type --</option>
                    <option value="repair">Repair</option>
                    <option value="replacement">Replacement</option>
                  </select>
                  <label
                    htmlFor={`request_type_${index}`}
                    className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                  >
                    Request Type <span className="text-red-500 m-2">*</span>
                  </label>
                </div>

                {/* Preview Order Number */}
                {form.warrantyType && form.requestType && (
                  <div className="flex flex-col items-start mb-5">
                    <div className="mb-2 text-sm text-[#0A2975] font-semibold">
                      Preview Order Number:{" "}
                      <span className="font-semibold">{computePreview(form, index)}</span>
                    </div>
                    {lastOrder && (
                      <div className="text-xs text-gray-600">
                        Last sequence: {baseSeq[form.requestType] || 0} • Last order: {lastOrder}
                      </div>
                    )}
                  </div>
                )}

                {/* Detailed fields only after warranty & request type */}
                {form.warrantyType && form.requestType && (
                  <>
                    {/* Receiving Date */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="date"
                        name="receivingDate"
                        id={`receiving_date_${index}`}
                        value={form.receivingDate}
                        onChange={(e) => handleChange(index, "receivingDate", e.target.value)}
                        className="block pt-5 px-0 w-fit lg:text-lg xl:text-lg text-sm text-[#0A2975] font-bold bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                        required
                      />
                      <label
                        htmlFor={`receiving_date_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white font-semibold"
                      >
                        Receiving Date <span className="text-red-500 m-2">*</span>
                      </label>
                    </div>

                    {/* Item Name */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="text"
                        name="instrumentName"
                        id={`instrument_name_${index}`}
                        value={form.instrumentName}
                        onChange={(e) => handleChange(index, "instrumentName", e.target.value)}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm 
               text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 
               appearance-none dark:text-white dark:border-gray-600 
               dark:focus:border-white focus:outline-none focus:ring-0 
               focus:border-[#0A2975] peer"
                        placeholder=" "
                        required
                      />
                      <label
                        htmlFor={`item_name_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black 
               dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 
               origin-[0] peer-placeholder-shown:scale-100 
               peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 
               peer-focus:-translate-y-4 peer-focus:text-[#0A2975] 
               peer-focus:dark:text-white"
                      >
                        Instrument Name <span className="text-red-500 m-2">*</span>
                      </label>
                    </div>

                    {/* Quantity */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="number"
                        min="1"
                        name="quantity"
                        id={`quantity_${index}`}
                        value={form.quantity}
                        onChange={(e) => handleChange(index, "quantity", Number(e.target.value))}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 
      bg-transparent border-0 border-b-2 border-gray-300 appearance-none 
      dark:text-white dark:border-gray-600 dark:focus:border-white 
      focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                        required
                      />
                      <label
                        htmlFor={`quantity_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black 
      dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 
      origin-[0] peer-placeholder-shown:scale-100 
      peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 
      peer-focus:-translate-y-4 peer-focus:text-[#0A2975] 
      peer-focus:dark:text-white"
                      >
                        Quantity <span className="text-red-500">*</span>
                      </label>
                    </div>


                    {/* Company Name */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="text"
                        name="companyName"
                        id={`company_name_${index}`}
                        value={form.companyName}
                        onChange={(e) => handleChange(index, "companyName", e.target.value)}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                        required
                      />
                      <label
                        htmlFor={`company_name_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                      >
                        Company Name <span className="text-red-500 m-2">*</span>
                      </label>
                    </div>

                    {/* Reseller */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="text"
                        name="reseller"
                        id={`reseller_${index}`}
                        value={form.reseller}
                        onChange={(e) => handleChange(index, "reseller", e.target.value)}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                      />
                      <label
                        htmlFor={`reseller_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                      >
                        Reseller (Optional)
                      </label>
                    </div>

                    {/* Server ID */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="text"
                        name="serverId"
                        id={`server_id_${index}`}
                        value={form.serverId}
                        onChange={(e) => handleChange(index, "serverId", e.target.value)}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                      />
                      <label
                        htmlFor={`server_id_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                      >
                        Server Id (Optional)
                      </label>
                    </div>

                    {/* Received By */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="text"
                        name="receivedBy"
                        id={`received_by_${index}`}
                        value={form.receivedBy}
                        onChange={(e) => handleChange(index, "receivedBy", e.target.value)}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                      />
                      <label
                        htmlFor={`received_by_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                      >
                        Received By (Optional)
                      </label>
                    </div>


                    {/* Handover Person */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="text"
                        name="handoverPerson"
                        id={`handover_person_${index}`}
                        value={form.handoverPerson}
                        onChange={(e) => handleChange(index, "handoverPerson", e.target.value)}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 
      bg-transparent border-0 border-b-2 border-gray-300 appearance-none 
      dark:text-white dark:border-gray-600 dark:focus:border-white 
      focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                      />
                      <label
                        htmlFor={`handover_person_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 
      duration-300 transform scale-75 top-0 left-0 origin-[0] 
      peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 
      peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] 
      peer-focus:dark:text-white"
                      >
                        Handover Person <span className="text-red-500 m-2">*</span>
                      </label>
                    </div>

                    {/* Department Person */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="text"
                        name="departmentPerson"
                        id={`department_person_${index}`}
                        value={form.departmentPerson}
                        onChange={(e) => handleChange(index, "departmentPerson", e.target.value)}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                      />
                      <label
                        htmlFor={`department_person_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                      >
                        Department Person (Optional)
                      </label>
                    </div>

                    {/* Issue Description */}
                    <div className="relative z-0 w-full mb-5 group">
                      <textarea
                        name="issueDescription"
                        id={`issue_description_${index}`}
                        value={form.issueDescription}
                        onChange={(e) => handleChange(index, "issueDescription", e.target.value)}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                        rows={3}
                      />
                      <label
                        htmlFor={`issue_description_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                      >
                        Issue Description (Optional)
                      </label>
                    </div>

                    {/* Remark */}
                    <div className="relative z-0 w-full mb-5 group">
                      <textarea
                        name="remark"
                        id={`remark_${index}`}
                        value={form.remark}
                        onChange={(e) => handleChange(index, "remark", e.target.value)}
                        className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                        rows={2}
                      />
                      <label
                        htmlFor={`remark_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                      >
                        Remark (Optional)
                      </label>
                    </div>

                    {/* Expected Delivery Date */}
                    <div className="relative z-0 w-full mb-5 group">
                      <input
                        type="date"
                        name="expectedDeliveryDate"
                        id={`expected_delivery_${index}`}
                        value={form.expectedDeliveryDate}
                        onChange={(e) => handleChange(index, "expectedDeliveryDate", e.target.value)}
                        min={form.receivingDate}
                        className="block pt-5 px-0 w-fit lg:text-lg xl:text-lg text-sm text-[#0A2975] font-bold bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                        placeholder=" "
                        required
                      />
                      <label
                        htmlFor={`expected_delivery_${index}`}
                        className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white font-semibold"
                      >
                        Expected Delivery Date <span className="text-red-500 m-2">*</span>
                      </label>
                    </div>
                  </>
                )}
              </div>
            </section>
          ))}

          <div className="flex justify-between">
            <button
              type="button"
              onClick={handleAddProduct}
              className="text-end max-w-fit bg-gray-200 text-black px-4 py-2 lg:text-lg xl:text-lg text-sm rounded-md hover:bg-gray-300 cursor-pointer transition duration-300"
              disabled={submitting}
            >
              + Add Product
            </button>

            {!submitting ? (
              <div className="justify-end flex gap-4">
                <button
                  type="submit"
                  className="text-end max-w-fit bg-[#0A2975] text-white px-3 py-1 font-semibold lg:text-xl xl:text-xl text-sm rounded-md hover:bg-[#092060] transition duration-300 cursor-pointer"
                >
                  Submit {forms.length > 1 ? `All (${forms.length})` : ""}
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  className="text-end max-w-fit bg-red-400 text-white px-3 py-1 font-semibold lg:text-xl xl:text-xl text-sm rounded-md hover:bg-red-500 transition duration-300 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="text-end max-w-fit bg-gray-400 text-white px-2 py-1 font-semibold lg:text-xl xl:text-xl text-sm rounded-md cursor-not-allowed"
                disabled
              >
                Submitting...
              </button>
            )}
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default RepairReplacementForm;